import type { FlexibleDashboardDrilldownRequest } from "@/types_api/backend/transport/flexible_dashboard_drilldown_request"
import type { FlexibleDashboardDrilldownResponse } from "@/types_api/backend/transport/flexible_dashboard_drilldown_response"
import type { HumanReviewDashboardDrilldownResponse } from "@/types_api/backend/transport/human_review_dashboard_drilldown_response"
import type { HumanReviewDrilldownRequest } from "@/types_api/backend/transport/human_review_drilldown_request"
import type { MetricValuesBySimulationRequest } from "@/types_api/backend/transport/metric_values_by_simulation_request"
import type { QueryDashboardBatchRequest } from "@/types_api/backend/transport/query_dashboard_batch_request"
import type { QueryDashboardBatchResponse } from "@/types_api/backend/transport/query_dashboard_batch_response"
import type { TargetCondition } from "@/types/metrics/contracts"

import type { GroupByType } from "../types/chart-types"
import type {
  DrillDownRunRecord,
  FilterType,
  MetricFilter,
} from "../types/core"

export const MAX_METRIC_IDS_PER_SIMULATION_VALUES_REQUEST = 10

interface DashboardMetadataFilterInput {
  key: string
  value: string
}

type DashboardBatchQueryKind =
  | "queryDashboardMetric"
  | "queryDashboardStatistic"
  | "queryHumanReviewMetric"
  | "queryAssigneeCallVolume"
  | "queryAssigneeCallHistory"

type DashboardBatchAggregation =
  | "count"
  | "avg"
  | "sum"
  | "min"
  | "max"
  | "success"
  | "p50"
  | "p90"
  | "p95"
  | "p99"

type DashboardBatchBucketInterval =
  | "15 minutes"
  | "1 hour"
  | "4 hours"
  | "1 day"
  | null

type DashboardBatchGroupBy =
  | "agent"
  | "assignee"
  | "mutation"
  | "persona"
  | "template"
  | "test_set"
  | null

interface DashboardFiltersInput {
  metric_ids?: string[] | null
  agent_ids?: string[] | null
  agent_mutation_ids?: string[] | null
  persona_ids?: string[] | null
  template_names?: string[] | null
  tag_filters?: string[] | null
  test_set_ids?: string[] | null
  test_case_ids?: string[] | null
  metadata?: DashboardMetadataFilterInput[] | null
}

interface DashboardMetricFilterInput {
  metric_id: string
  operator: ">" | "<" | ">=" | "<=" | "=" | "!="
  value: number | string
  metric_output_type: "float" | "string"
}

interface DashboardTargetConditionInput {
  comparison_operator: "gt" | "gte" | "lt" | "lte" | "eq" | "neq" | "in" | "nin"
  target_float?: number | null
  target_values?: string[] | null
}

type HumanReviewDrilldownRequestWithFilters = HumanReviewDrilldownRequest & {
  filters?: DashboardFiltersInput
  group_by?: "assignee"
  dimension_value?: string
  metric_filter?: DashboardMetricFilterInput[]
}

export function isTransportDashboardQueryLoading(
  query: { data: unknown; error: unknown; isLoading: boolean },
  enabled = true,
): boolean {
  return enabled && (query.isLoading || (query.data == null && !query.error))
}

function serializeDashboardFilters(
  filters?: FilterType,
): DashboardFiltersInput | undefined {
  if (!filters) {
    return undefined
  }

  const serializedFilters: DashboardFiltersInput = {}

  if (filters.metricIds?.length) {
    serializedFilters.metric_ids = filters.metricIds
  }
  if (filters.agentIds?.length) {
    serializedFilters.agent_ids = filters.agentIds
  }
  if (filters.agentMutationIds?.length) {
    serializedFilters.agent_mutation_ids = filters.agentMutationIds
  }
  if (filters.personaIds?.length) {
    serializedFilters.persona_ids = filters.personaIds
  }
  if (filters.templateNames?.length) {
    serializedFilters.template_names = filters.templateNames
  }
  if (filters.tagFilters?.length) {
    serializedFilters.tag_filters = filters.tagFilters
  }
  if (filters.testSetIds?.length) {
    serializedFilters.test_set_ids = filters.testSetIds
  }
  if (filters.testCaseIds?.length) {
    serializedFilters.test_case_ids = filters.testCaseIds
  }
  if (filters.metadata?.length) {
    serializedFilters.metadata = filters.metadata
  }

  return Object.keys(serializedFilters).length > 0
    ? serializedFilters
    : undefined
}

const DASHBOARD_FILTER_INPUT_KEYS = new Set<string>([
  "metric_ids",
  "agent_ids",
  "agent_mutation_ids",
  "persona_ids",
  "template_names",
  "tag_filters",
  "test_set_ids",
  "test_case_ids",
  "metadata",
])

const DASHBOARD_FILTER_SNAKE_CASE_KEYS = new Set<string>([
  "metric_ids",
  "agent_ids",
  "agent_mutation_ids",
  "persona_ids",
  "template_names",
  "tag_filters",
  "test_set_ids",
  "test_case_ids",
])

function hasFilterValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.length > 0
  }

  return value != null
}

function isDashboardFiltersInput(
  filters: DashboardFiltersInput | FilterType,
): filters is DashboardFiltersInput {
  return Object.keys(filters).some((key) =>
    DASHBOARD_FILTER_SNAKE_CASE_KEYS.has(key),
  )
}

function normalizeDashboardFilters(
  filters?: DashboardFiltersInput | FilterType | null,
): DashboardFiltersInput | undefined {
  if (!filters) {
    return undefined
  }

  if (!isDashboardFiltersInput(filters)) {
    return serializeDashboardFilters(filters)
  }

  const entries = Object.entries(filters).filter(
    ([key, value]) =>
      DASHBOARD_FILTER_INPUT_KEYS.has(key) && hasFilterValue(value),
  )

  return entries.length > 0
    ? (Object.fromEntries(entries) as DashboardFiltersInput)
    : undefined
}

function normalizeDashboardGroupBy(
  query: DashboardBatchQueryInput,
): DashboardBatchGroupBy {
  const groupBy = query.groupBy ?? query.group_by ?? null

  if (groupBy === "assignee" && query.kind !== "queryHumanReviewMetric") {
    return null
  }

  return groupBy
}

function serializeDashboardMetricFilter(
  metricFilter?: MetricFilter[],
): DashboardMetricFilterInput[] | undefined {
  if (!metricFilter?.length) {
    return undefined
  }

  return metricFilter.map((filter) => ({
    metric_id: filter.metricId,
    operator: filter.operator,
    value: filter.value,
    metric_output_type: filter.metricOutputType,
  }))
}

function isDashboardMetricFilterInput(
  filter: DashboardMetricFilterInput | MetricFilter,
): filter is DashboardMetricFilterInput {
  return "metric_id" in filter || "metric_output_type" in filter
}

function normalizeDashboardMetricFilter(
  metricFilter?: MetricFilter[] | DashboardMetricFilterInput[] | null,
): DashboardMetricFilterInput[] | undefined {
  if (!metricFilter?.length) {
    return undefined
  }

  const normalizedMetricFilter = metricFilter.map((filter) =>
    isDashboardMetricFilterInput(filter)
      ? {
          metric_id: filter.metric_id,
          operator: filter.operator,
          value: filter.value,
          metric_output_type: filter.metric_output_type,
        }
      : {
          metric_id: filter.metricId,
          operator: filter.operator,
          value: filter.value,
          metric_output_type: filter.metricOutputType,
        },
  )

  return normalizedMetricFilter.length > 0 ? normalizedMetricFilter : undefined
}

function serializeTargetCondition(
  targetCondition: TargetCondition,
): DashboardTargetConditionInput {
  return {
    comparison_operator: targetCondition.comparison_operator,
    target_float:
      "target_float" in targetCondition ? targetCondition.target_float : null,
    target_values:
      "target_values" in targetCondition ? targetCondition.target_values : null,
  }
}

function serializeTargetConditionsByMetricId(
  targetConditionsByMetricId?: Record<string, TargetCondition>,
): Record<string, DashboardTargetConditionInput> | undefined {
  if (!targetConditionsByMetricId) {
    return undefined
  }

  return Object.fromEntries(
    Object.entries(targetConditionsByMetricId).map(([metricId, condition]) => [
      metricId,
      serializeTargetCondition(condition),
    ]),
  )
}

interface DashboardBatchQueryInput {
  kind: DashboardBatchQueryKind
  aggregation?: DashboardBatchAggregation
  monitoring?: boolean
  assignee?: string
  type?: "chart" | "table"
  query_id?: string | null
  queryId?: string | null
  metric_id?: string | null
  metricId?: string | null
  metric_ids?: string[] | null
  metricIds?: string[] | null
  metric_output_type?: "float" | "string" | null
  metricOutputType?: "float" | "string" | null
  start_date?: string | null
  startDate?: string | null
  end_date?: string | null
  endDate?: string | null
  bucket_interval?: DashboardBatchBucketInterval
  bucketInterval?: DashboardBatchBucketInterval
  skip_bucketing?: boolean
  skipBucketing?: boolean
  raw_values?: boolean
  rawValues?: boolean
  group_by?: DashboardBatchGroupBy
  groupBy?: GroupByType
  group_by_metadata_key?: string | null
  groupByMetadataKey?: string | null
  filters?: FilterType | DashboardFiltersInput | null
  metric_filter?: MetricFilter[] | DashboardMetricFilterInput[] | null
  metricFilter?: MetricFilter[]
  target_conditions_by_metric_id?:
    | Record<string, TargetCondition>
    | Record<string, DashboardTargetConditionInput>
    | null
  targetConditionsByMetricId?: Record<string, TargetCondition>
  timezone?: string
}

export function serializeDashboardBatchQuery(
  query: DashboardBatchQueryInput,
): QueryDashboardBatchRequest["queries"][number] {
  const groupByMetadataKey =
    query.groupByMetadataKey ?? query.group_by_metadata_key
  const groupBy = groupByMetadataKey ? null : normalizeDashboardGroupBy(query)

  return {
    kind: query.kind,
    ...(query.aggregation ? { aggregation: query.aggregation } : {}),
    ...(query.monitoring !== undefined ? { monitoring: query.monitoring } : {}),
    ...(query.assignee ? { assignee: query.assignee } : {}),
    ...(query.type ? { type: query.type } : {}),
    query_id: query.queryId ?? query.query_id,
    metric_id: query.metricId ?? query.metric_id,
    metric_ids: query.metricIds ?? query.metric_ids,
    metric_output_type: query.metricOutputType ?? query.metric_output_type,
    start_date: query.startDate ?? query.start_date,
    end_date: query.endDate ?? query.end_date,
    bucket_interval: query.bucketInterval ?? query.bucket_interval,
    skip_bucketing: query.skipBucketing ?? query.skip_bucketing,
    raw_values: query.rawValues ?? query.raw_values,
    ...(groupBy ? { group_by: groupBy } : {}),
    ...(groupByMetadataKey
      ? {
          group_by_metadata_key: groupByMetadataKey,
        }
      : {}),
    ...(query.timezone ? { timezone: query.timezone } : {}),
    filters: normalizeDashboardFilters(query.filters),
    metric_filter: normalizeDashboardMetricFilter(
      query.metricFilter ?? query.metric_filter,
    ),
    target_conditions_by_metric_id: serializeTargetConditionsByMetricId(
      query.targetConditionsByMetricId ??
        (query.target_conditions_by_metric_id as
          | Record<string, TargetCondition>
          | undefined),
    ),
  } as QueryDashboardBatchRequest["queries"][number]
}

export function getDashboardBatchRows<T>(
  response: QueryDashboardBatchResponse | undefined,
  index = 0,
): T[] {
  return (response?.results?.[index]?.rows ?? []) as T[]
}

export function serializeFlexibleDashboardDrilldownRequest(request: {
  metricId: string
  startDate: string
  endDate: string
  monitoring: boolean
  stringValue?: string
  filters?: FilterType
  groupBy?: GroupByType
  groupByMetadataKey?: string
  metadataGroupKind?: "value" | "remainder"
  metadataTopValues?: string[]
  dimensionValue: string
  timezone: string
  metricFilter?: MetricFilter[]
}): FlexibleDashboardDrilldownRequest {
  return {
    metric_id: request.metricId,
    start_date: request.startDate,
    end_date: request.endDate,
    monitoring: request.monitoring,
    string_value: request.stringValue,
    filters: serializeDashboardFilters(request.filters),
    ...(!request.groupByMetadataKey &&
    request.groupBy &&
    request.groupBy !== "assignee"
      ? { group_by: request.groupBy }
      : {}),
    ...(request.groupByMetadataKey
      ? {
          group_by_metadata_key: request.groupByMetadataKey,
          metadata_group_kind: request.metadataGroupKind,
          ...(request.metadataGroupKind === "remainder"
            ? { metadata_top_values: request.metadataTopValues }
            : {}),
        }
      : {}),
    dimension_value: request.dimensionValue,
    timezone: request.timezone,
    metric_filter: serializeDashboardMetricFilter(request.metricFilter),
  }
}

export function serializeHumanReviewDashboardDrilldownRequest(request: {
  metricId: string
  startDate: string
  endDate: string
  stringValue?: string
  filters?: FilterType
  groupBy?: GroupByType
  dimensionValue?: string
  metricFilter?: MetricFilter[]
}): HumanReviewDrilldownRequestWithFilters {
  return {
    metric_id: request.metricId,
    start_date: request.startDate,
    end_date: request.endDate,
    string_value: request.stringValue,
    filters: serializeDashboardFilters(request.filters),
    ...(request.groupBy === "assignee" ? { group_by: request.groupBy } : {}),
    ...(request.dimensionValue != null
      ? { dimension_value: request.dimensionValue }
      : {}),
    metric_filter: serializeDashboardMetricFilter(request.metricFilter),
  }
}

export function serializeMetricValuesBySimulationRequest(request: {
  simulationOutputIds: string[]
  metricIds: string[]
}): MetricValuesBySimulationRequest {
  if (request.metricIds.length > MAX_METRIC_IDS_PER_SIMULATION_VALUES_REQUEST) {
    throw new Error(
      `Metric values by simulation requests support at most ${MAX_METRIC_IDS_PER_SIMULATION_VALUES_REQUEST} metric ids.`,
    )
  }

  return {
    simulation_output_ids: request.simulationOutputIds,
    metric_ids:
      request.metricIds as MetricValuesBySimulationRequest["metric_ids"],
  }
}

export function normalizeDrilldownRuns(
  runs?:
    | FlexibleDashboardDrilldownResponse["runs"]
    | HumanReviewDashboardDrilldownResponse["runs"],
): DrillDownRunRecord[] {
  if (!runs) {
    return []
  }

  return runs.map((run) => ({
    simulation_output_id: run.simulation_output_id,
    run_id: run.run_id,
    occurred_at: run.occurred_at,
    created_at: run.created_at ?? undefined,
    value: run.value,
    metric_id: run.metric_id,
    agent_id: run.agent_id ?? undefined,
    persona_id: run.persona_id ?? undefined,
    template_name: run.template_name ?? undefined,
    test_set_id: run.test_set_id ?? undefined,
  }))
}
